import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, FileText, AlertCircle, Loader2 } from "lucide-react";
import { api, API_URL, formatError } from "../lib/api.js";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-ronil-light flex items-center justify-center px-4 py-10">
    <div className="w-full max-w-lg bg-white border border-slate-200 rounded-lg shadow-elevation p-7 sm:p-9" data-testid="public-quote-approval-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-ronil-navy text-white font-display font-bold text-lg rounded-sm flex items-center justify-center">R</div>
        <div>
          <div className="font-heading font-black text-base tracking-[0.1em] text-ronil-navy">RONIL</div>
          <div className="text-[9px] text-slate-500 uppercase tracking-[0.3em]">Bate-chapas e Pintura</div>
        </div>
      </div>
      {children}
    </div>
  </div>
);

const Done = ({ status }) => (
  <div className={`flex items-start gap-3 p-4 rounded-md border ${status === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`} data-testid="public-quote-result">
    {status === "approved" ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <XCircle className="w-5 h-5 shrink-0" />}
    <div className="text-sm"><b>Orçamento {status === "approved" ? "aprovado" : "rejeitado"}.</b> Obrigado — a oficina foi notificada.</div>
  </div>
);

export default function PublicQuoteApprovalPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/public/bps-damage-approval/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(formatError(e.response?.data?.detail, "Link inválido.")));
  }, [token]);

  const decide = async (approved) => {
    setBusy(true); setError("");
    try { const { data } = await api.post(`/public/bps-damage-approval/${token}`, { approved, comment }); setResult(data.status); }
    catch (e) { setError(formatError(e.response?.data?.detail, "Não foi possível registar a decisão.")); }
    finally { setBusy(false); }
  };

  if (!info && !error) return <Shell><div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div></Shell>;
  if (!info) return <Shell><div className="flex items-start gap-2 text-sm text-red-800" data-testid="public-quote-error"><AlertCircle className="w-4 h-4 mt-0.5" />{error}</div></Shell>;

  const decided = result || (info.status !== "pending_approval" ? info.status : null);
  return (
    <Shell>
      <div className="text-[10px] uppercase tracking-[0.25em] text-ronil-sand font-heading font-bold mb-2">Orçamento adicional · {info.case_number}</div>
      <h1 className="font-display text-2xl font-bold text-ronil-navy leading-tight mb-1">{info.vehicle_registration} · {info.brand} {info.model}</h1>
      <p className="text-sm text-slate-500 mb-5">Cliente: {info.customer_name} · Dealer: {info.branch}</p>
      <div className="bg-[#F4F6F8] border-l-4 border-ronil-navy rounded-sm p-3 text-sm mb-3" data-testid="public-quote-description">{info.description}</div>
      {info.estimated_amount ? <p className="text-sm mb-3">Valor estimado: <b>{Number(info.estimated_amount).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })} MZN</b></p> : null}
      {info.has_quote && !info.expired && (
        <a href={`${API_URL}/public/bps-damage-approval/${token}/quote`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-ronil-navy hover:underline mb-5" data-testid="public-quote-download">
          <FileText className="w-4 h-4" /> Ver orçamento anexado
        </a>
      )}
      {decided ? <Done status={decided} /> : info.used ? (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="public-quote-superseded">Este link foi substituído por um mais recente. Use o último email recebido da oficina.</div>
      ) : info.expired ? (
        <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md p-3" data-testid="public-quote-expired">Este link expirou. Contacte a oficina para receber um novo.</div>
      ) : (
        <>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold mb-1.5">Comentário (opcional)</label>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm h-20 mb-4" data-testid="public-quote-comment" />
          {error && <div className="text-sm text-red-800 mb-3" data-testid="public-quote-error">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => decide(true)} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md py-2.5 text-sm font-semibold disabled:opacity-60 transition-colors" data-testid="public-quote-approve">Aprovar</button>
            <button onClick={() => decide(false)} disabled={busy} className="bg-white border border-red-600 text-red-700 hover:bg-red-50 rounded-md py-2.5 text-sm font-semibold disabled:opacity-60 transition-colors" data-testid="public-quote-reject">Rejeitar</button>
          </div>
        </>
      )}
    </Shell>
  );
}
